import { Button, Card, MonoLabel, Pill } from '../primitives';
import {
  formatTierSeatLabel,
  TIER_DISPLAY_NAME
} from '../../lib/subscriptionTiers';
import type {
  SubscriptionStatus,
  SubscriptionTier
} from '../../lib/vm/useBillingVM';

export { TIER_DISPLAY_NAME };

/** Tier ordinal — `'free' < 't25' < 't100' < 't200' < 't500'`. Mirrors the
 *  Postgres enum declaration order so this is the canonical compare. */
const TIER_ORDER: ReadonlyArray<SubscriptionTier> = [
  'free',
  't25',
  't100',
  't200',
  't500'
];

export interface TierCardProps {
  tier: SubscriptionTier;
  /** USD per month from `subscription_tier_config.monthly_price_usd`. */
  price: number;
  /** Seat cap from `subscription_tier_config.max_seats`. */
  maxSeats: number;
  /** The viewer's current tier — drives the CTA label + current-highlight. */
  currentTier: SubscriptionTier;
  /** The viewer's current subscription status — used to detect the
   *  `incomplete` recovery case where `current_tier` is set but checkout was
   *  abandoned mid-flow; the CTA for the matching tier then becomes
   *  `Resume checkout` instead of the disabled `Current plan`. (Cluster 5K.) */
  currentStatus?: SubscriptionStatus | null;
  /** True while another action is in flight; disables every TierCard's CTA. */
  busy?: boolean;
  /** True when this org is past_due / canceled / scheduled to cancel and the
   *  page wants to keep CTAs visible but inert. */
  disabled?: boolean;
  onSelect: (tier: SubscriptionTier) => void;
}

function tierOrdinal(t: SubscriptionTier): number {
  return TIER_ORDER.indexOf(t);
}

function ctaFor(
  tier: SubscriptionTier,
  currentTier: SubscriptionTier,
  currentStatus: SubscriptionStatus | null | undefined
): { label: string; variant: 'primary' | 'default' | 'ghost'; disabled: boolean } {
  if (tier === currentTier) {
    // Cluster 5K — Resume Checkout recovery. When `current_tier` was set but
    // the user closed the checkout modal before paying, the row stays at
    // `status='incomplete'`. The matching TierCard would otherwise render
    // disabled "Current plan", leaving no UI path back to checkout. Re-route
    // the click through `onSelect(tier)` — `createSubscription({ tier })`
    // already auto-cancels the stale sub + mints a fresh PaymentIntent
    // (edge function `stripe-create-subscription` 5H recovery branch).
    if (currentStatus === 'incomplete' || currentStatus === 'incomplete_expired') {
      return { label: 'Resume checkout', variant: 'primary', disabled: false };
    }
    return { label: 'Current plan', variant: 'ghost', disabled: true };
  }
  const next = tierOrdinal(tier);
  const cur = tierOrdinal(currentTier);
  if (tier === 'free') {
    // Dropping to free is the cancellation flow, not a "downgrade" CTA — the
    // page's Cancel-subscription footer button handles it. Keep the button
    // inert here so users use the correct affordance.
    return { label: 'Cancel to downgrade', variant: 'ghost', disabled: true };
  }
  if (next > cur) {
    return {
      label: currentTier === 'free' ? 'Choose plan' : 'Upgrade',
      variant: 'primary',
      disabled: false
    };
  }
  return { label: 'Downgrade', variant: 'default', disabled: false };
}

/**
 * One column in the `/settings/billing` pricing grid. Composes `<Card>` with
 * a price/cap stack and a CTA whose label depends on the relationship of
 * `tier` to `currentTier`. Visual highlight for the current tier is keyed off
 * `data-current="true"` on the card root so the page CSS can target it
 * without an extra wrapper.
 */
export function TierCard({
  tier,
  price,
  maxSeats,
  currentTier,
  currentStatus,
  busy,
  disabled,
  onSelect
}: TierCardProps) {
  const isCurrent = tier === currentTier;
  const cta = ctaFor(tier, currentTier, currentStatus ?? null);
  const seatLabel = formatTierSeatLabel(maxSeats);
  const buttonDisabled = cta.disabled || busy || disabled;

  return (
    <Card
      className="set-billing-tier-card"
      data-current={isCurrent ? 'true' : 'false'}
      data-tier={tier}
      data-testid={`billing-tier-card-${tier}`}
    >
      <header className="set-billing-tier-head">
        <h3 className="set-billing-tier-name">{TIER_DISPLAY_NAME[tier]}</h3>
        {isCurrent ? (
          <Pill status="active" data-testid={`billing-tier-current-${tier}`}>
            current
          </Pill>
        ) : null}
      </header>
      <div className="set-billing-tier-price">
        <span className="set-billing-tier-price-amount">${price}</span>
        <MonoLabel size="sm" tracking="wide" tone="muted">
          /mo
        </MonoLabel>
      </div>
      <MonoLabel
        size="sm"
        tracking="wide"
        tone="muted"
        as="div"
        className="set-billing-tier-seats"
      >
        {seatLabel}
      </MonoLabel>
      <div className="set-billing-tier-cta">
        <Button
          type="button"
          variant={cta.variant}
          size="md"
          disabled={buttonDisabled}
          onClick={() => onSelect(tier)}
          data-testid={`billing-tier-cta-${tier}`}
        >
          {cta.label}
        </Button>
      </div>
    </Card>
  );
}
